import type { Rect } from './types';

export interface KinematicBody extends Rect {
  velocityX: number;
  velocityY: number;
}

export interface SolidCollider extends Rect {
  id: string;
  kind: 'tile' | 'block' | 'pipe';
}

export interface AxisMoveResult {
  collider: SolidCollider | null;
  collided: boolean;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function approach(value: number, target: number, maximumDelta: number): number {
  if (value < target) return Math.min(target, value + maximumDelta);
  if (value > target) return Math.max(target, value - maximumDelta);
  return target;
}

export function overlaps(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

export function moveHorizontal(
  body: KinematicBody,
  deltaX: number,
  solids: readonly SolidCollider[],
): AxisMoveResult {
  if (deltaX === 0) return { collider: null, collided: false };
  body.x += deltaX;
  let hit: SolidCollider | null = null;
  for (const solid of solids) {
    if (!overlaps(body, solid)) continue;
    hit = solid;
    if (deltaX > 0) body.x = Math.min(body.x, solid.x - body.width);
    else body.x = Math.max(body.x, solid.x + solid.width);
  }
  if (hit) body.velocityX = 0;
  return { collider: hit, collided: hit !== null };
}

export function moveVertical(
  body: KinematicBody,
  deltaY: number,
  solids: readonly SolidCollider[],
): AxisMoveResult {
  if (deltaY === 0) return { collider: null, collided: false };
  body.y += deltaY;
  let hit: SolidCollider | null = null;
  for (const solid of solids) {
    if (!overlaps(body, solid)) continue;
    hit = solid;
    if (deltaY > 0) body.y = Math.min(body.y, solid.y - body.height);
    else body.y = Math.max(body.y, solid.y + solid.height);
  }
  if (hit) body.velocityY = 0;
  return { collider: hit, collided: hit !== null };
}
